/**
 * Admin y superadmin son los únicos que crean, editan y borran planes.
 * El mismo criterio ya se usa en reportes de weight loss (rol 1 y 2).
 */
export function isMembershipPlanAdmin() {
  try {
    const raw = localStorage.getItem('userData')
    if (!raw) return false
    const user = JSON.parse(raw)
    const roles = Array.isArray(user?.roles) ? user.roles : []
    return roles.some((role) => {
      const id = Number(role?.id ?? role?.role_id)
      const name = String(role?.name || '').toLowerCase()
      return id === 1 || id === 2 || name === 'superadmin' || name === 'admin'
    })
  } catch (error) {
    return false
  }
}

/**
 * Mensaje de 422, 403 o 502 de Stripe, sin inventar texto si el backend ya lo trae.
 */
export function membershipApiMessage(error, fallback) {
  const data = error && error.response && error.response.data
  if (!data) return fallback
  const raw = data.message != null ? data.message : data.error
  if (Array.isArray(raw)) return raw.filter(Boolean).join(' ') || fallback
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  if (data.errors && typeof data.errors === 'object') {
    const first = Object.values(data.errors)[0]
    if (Array.isArray(first) && first[0]) return String(first[0])
    if (typeof first === 'string') return first
  }
  return fallback
}

/**
 * Campos opcionales del plan. El trimestral vacío se envía null (no se ofrece).
 * El resto, si queda vacío, no se envía para no pisar el valor que ya tiene el plan.
 */
export const PLAN_KIND_OPTIONS = [
  { value: 'individual', text: 'Individual' },
  { value: 'family', text: 'Familiar' },
  { value: 'company', text: 'Empresas' },
]

export const BENEFIT_AUDIENCE_OPTIONS = [
  { value: '', text: 'Both' },
  { value: 'adult', text: 'Adult' },
  { value: 'child', text: 'Child' },
]

/**
 * Campos del plan Familiar. En otro tipo de plan no se envían, para no pisar un individual.
 * @param {Object} formData
 */
export function applyFamilyPlanFields(formData) {
  const kind = formData.plan_kind || 'individual'
  formData.plan_kind = kind
  if (kind !== 'family') {
    delete formData.min_members
    delete formData.child_min_age
    delete formData.adult_min_age
    delete formData.extra_member_amount
    delete formData.family_tiers
    return formData
  }

  ;['min_members', 'child_min_age', 'adult_min_age', 'extra_member_amount'].forEach((key) => {
    const value = formData[key]
    if (value === '' || value === null || value === undefined) {
      delete formData[key]
    } else {
      formData[key] = Number(value)
    }
  })

  const tiers = (Array.isArray(formData.family_tiers) ? formData.family_tiers : [])
    .filter((tier) => tier && tier.members !== '' && tier.members != null && tier.amount !== '' && tier.amount != null)
    .map((tier) => ({
      members: Number(tier.members),
      amount: Number(tier.amount),
    }))
  // El backend borra todos los tramos si recibe family_tiers vacío, y show() no los devuelve:
  // sin tramos escritos no se envía el campo para conservar los actuales.
  if (tiers.length) {
    formData.family_tiers = tiers
  } else {
    delete formData.family_tiers
  }

  return formData
}

export function applyMembershipPricingFields(formData) {
  const quarter = formData.quarter_price_multiplier
  if (quarter === '' || quarter === null || quarter === undefined) {
    formData.quarter_price_multiplier = null
  } else {
    formData.quarter_price_multiplier = Number(quarter)
  }

  ;['year_price_multiplier', 'enrollment_fee', 'reactivation_fee', 'uncovered_discount_percentage'].forEach((key) => {
    const value = formData[key]
    if (value === '' || value === null || value === undefined) {
      delete formData[key]
    } else {
      formData[key] = Number(value)
    }
  })

  return formData
}

/** El detalle por id trae el id de cada beneficio; el listado a veces no. */
export function unwrapMembershipPlan(payload, fallback) {
  const body = payload && payload.data && !Array.isArray(payload.data) ? payload.data : payload
  const plan = body && body.registro ? body.registro : body
  if (!plan || typeof plan !== 'object') return fallback
  const details = plan.parameters_details
  return {
    ...(fallback || {}),
    ...plan,
    parameters_details: Array.isArray(details) && details.length
      ? details
      : (fallback && fallback.parameters_details) || [],
  }
}
